import { Component, computed, input } from '@angular/core';

export type BadgeVariant = 'none' | 'check' | 'warning';

export enum Shape {
  Circle = 'circle',
  Hexagon = 'hexagon',
  Shield = 'shield',
}

@Component({
    selector: 'app-badge-variant',
    imports: [],
    templateUrl: './badge-variant.component.html',
    styleUrl: './badge-variant.component.scss'
})
export class BadgeVariantComponent {
  readonly Shape = Shape;

  badge = input<BadgeVariant>('check');
  shape = input<Shape>(Shape.Circle);
  label = input<string | number>('MI');
  backgroundColor = input('var(--badge-mi-background)');
  tooltip = input<string | null>(null);
  size = input('24');

  readonly sizeNumber = computed(() => {
    const parsed = Number(this.size());
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 24;
  });

  readonly safeLabel = computed(() => String(this.label() ?? ''));
  readonly labelLength = computed(() => Math.min(this.safeLabel().length, 4));

  readonly viewBoxWidth = computed(() => {
    if (this.shape() !== Shape.Hexagon) {
      return 24;
    }

    return 18 + this.labelLength() * 6;
  });

  readonly svgWidth = computed(() => (this.sizeNumber() * this.viewBoxWidth()) / 24);
  readonly centerX = computed(() => this.viewBoxWidth() / 2);
  readonly resolvedBackgroundColor = computed(() => {
    const color = this.backgroundColor()?.trim() ?? '';
    if (
      (color.startsWith("'") && color.endsWith("'")) ||
      (color.startsWith('"') && color.endsWith('"'))
    ) {
      return color.slice(1, -1).trim();
    }

    return color || 'var(--badge-mi-background)';
  });

  readonly mainStroke = computed(() => {
    if (this.shape() === Shape.Hexagon || this.shape() === Shape.Shield) {
      return 'var(--badge-stroke-default)';
    }

    if (this.badge() === 'warning') {
      return 'var(--badge-stroke-warning)';
    }

    if (this.badge() === 'check') {
      return 'var(--badge-stroke-check)';
    }

    return 'var(--badge-stroke-default)';
  });

  readonly hexagonPoints = computed(() => {
    const width = this.viewBoxWidth();
    const shoulderInset = 6;
    const tipInset = 0.8;
    const centerY = 12;
    const maxHeight = 20;
    const strokeWidth = 1.5;
    const halfHeight = (maxHeight - strokeWidth) / 2;
    const topY = centerY - halfHeight;
    const bottomY = centerY + halfHeight;
    return `${shoulderInset},${topY} ${width - shoulderInset},${topY} ${width - tipInset},${centerY} ${width - shoulderInset},${bottomY} ${shoulderInset},${bottomY} ${tipInset},${centerY}`;
  });

  readonly shieldPath = computed(() => {
    const width = this.viewBoxWidth();
    const middle = width / 2;
    const side = 2.8;
    const top = 4.2;
    const shoulderY = 10.4;
    const tipY = 22;
    return `M${middle},${tipY} C${middle + 4.6},19.8 ${width - side},15.4 ${width - side},${shoulderY} V${top} C${middle + 3.9},4 ${middle + 1.9},2.9 ${middle},1.8 C${middle - 1.9},2.9 ${middle - 3.9},4 ${side},${top} V${shoulderY} C${side},15.4 ${middle - 4.6},19.8 ${middle},${tipY} Z`;
  });

  readonly shieldLeftFacetPath = computed(() => {
    const width = this.viewBoxWidth();
    const middle = width / 2;
    const side = 4.2;
    return `M${middle},20.2 C${middle - 2.7},18.4 ${side},14.9 ${side},10.7 V5.4 C${middle - 2.1},5.1 ${middle - 1.1},4.7 ${middle},4.2 Z`;
  });

  readonly shieldRightFacetPath = computed(() => {
    const width = this.viewBoxWidth();
    const middle = width / 2;
    const side = width - 4.2;
    return `M${middle},20.2 C${middle + 2.7},18.4 ${side},14.9 ${side},10.7 V5.4 C${middle + 2.1},5.1 ${middle + 1.1},4.7 ${middle},4.2 Z`;
  });

  readonly shieldRidgePath = computed(() => {
    const middle = this.viewBoxWidth() / 2;
    return `M${middle},4.2 L${middle},20.2`;
  });

  readonly shieldTopHighlightPath = computed(() => {
    const width = this.viewBoxWidth();
    const middle = width / 2;
    const side = 4.6;
    return `M${side},5.8 C${middle - 2.2},4.8 ${middle + 2.2},4.8 ${width - side},5.8`;
  });
}
